// Dynamic API Base URL - automatically uses current domain
const getApiBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Otherwise, construct from current location
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // Use port 3000 if hostname is localhost, otherwise use window.location.port
  const port = hostname === 'localhost' ? '4000' : (window.location.port || '');
  
  // Use current domain with /api path
  return `${protocol}//${hostname}${port ? `:${port}` : ''}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Debug: Log the API base URL being used
console.log('[API Service] API Base URL:', API_BASE_URL);
console.log('[API Service] REACT_APP_API_BASE_URL env:', process.env.REACT_APP_API_BASE_URL);

class ApiService {
  // Get all tabs with questions
  async getTabs(assessmentType = 'AIReadiness') {
    try {
      const url = new URL(`${API_BASE_URL}/questions/tabs`);
      url.searchParams.append('assessment_type', assessmentType);
      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching tabs:', error);
      throw error;
    }
  }

  // Get questions for a specific tab
  async getQuestionsForTab(tabId) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/tabs/${tabId}/questions`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching questions for tab:', error);
      throw error;
    }
  }

  // Get maturity levels for a tab
  async getMaturityLevels(tabId, assessmentType = 'AIReadiness') {
    const url = new URL(`${API_BASE_URL}/questions/tabs/${encodeURIComponent(tabId)}/levels`);
    url.searchParams.append('assessment_type', assessmentType);
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  // Get selected level for current session
  async getSelectedLevel(email, tabId) {
    const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/tabs/${encodeURIComponent(tabId)}/level`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  // Save selected level for current session
  async saveSelectedLevel(email, tabId, selectedLevel) {
    const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/tabs/${encodeURIComponent(tabId)}/level`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedLevel })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  // Create or update user session
  async createSession(userData) {
    try {
      const url = `${API_BASE_URL}/questions/sessions`;
      console.log('[API] Creating session - URL:', url);
      console.log('[API] Creating session - Data:', userData);
      console.log('[API] Full API Base URL:', API_BASE_URL);
      console.log('[API] Environment variable:', process.env.REACT_APP_API_BASE_URL);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      console.log('[API] Session creation response status:', response.status, response.statusText);
      console.log('[API] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[API] Session creation failed:', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('[API] Session created successfully:', result);
      return result;
    } catch (error) {
      console.error('[API] Error creating session:', error);
      console.error('[API] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        url: `${API_BASE_URL}/questions/sessions`,
        userData: userData
      });
      
      // Provide more specific error messages
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        const connectionError = new Error(`Cannot connect to backend at ${API_BASE_URL}. Please ensure the backend server is running on port 4000.`);
        connectionError.originalError = error;
        throw connectionError;
      }
      
      throw error;
    }
  }

  // Save answers for a user
  async saveAnswers(email, answers) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error saving answers:', error);
      throw error;
    }
  }

  // Get answers for a user
  async getAnswers(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/answers`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching answers:', error);
      throw error;
    }
  }

  // Complete a session
  async completeSession(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error completing session:', error);
      throw error;
    }
  }

  // Get user statistics
  async getUserStats(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/stats`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }

  // Get real-time AI analysis
  async getRealtimeAnalysis(email, questionId = null, answer = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/analyze-realtime`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId,
          answer
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        console.error('🚨 [AI Service] Real-time analysis failed:', {
          timestamp: new Date().toISOString(),
          email,
          questionId,
          status: response.status,
          statusText: response.statusText,
          errorMessage: error.message,
          errorData: errorData,
          stack: error.stack
        });
        throw error;
      }
      return await response.json();
    } catch (error) {
      console.error('🚨 [AI Service] Real-time analysis error:', {
        timestamp: new Date().toISOString(),
        email,
        questionId,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        errorResponse: error.response
      });
      throw error;
    }
  }

  // Get comprehensive maturity analysis
  async getMaturityAnalysis(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/analyze-maturity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        console.error('🚨 [AI Service] Maturity analysis failed:', {
          timestamp: new Date().toISOString(),
          email,
          status: response.status,
          statusText: response.statusText,
          errorMessage: error.message,
          errorData: errorData,
          stack: error.stack
        });
        throw error;
      }
      return await response.json();
    } catch (error) {
      console.error('🚨 [AI Service] Maturity analysis error:', {
        timestamp: new Date().toISOString(),
        email,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        errorResponse: error.response
      });
      throw error;
    }
  }

  // Submit dimension selections and notes for AI assessment
  // Supports both regular (onProgress = null) and streaming (onProgress callback) modes
  async submitDimensionAssessment(email, payload, onProgress = null) {
    try {
      console.log('[API] submitDimensionAssessment →', { email, payload, streaming: !!onProgress });
      
      // Use streaming if progress callback is provided
      if (onProgress) {
        const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/assess-dimensions?stream=true&format=html`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const err = await response.json().catch(()=>({}));
          // Extract actual error message from server response
          // Handle ValidationError responses which may have validationErrors array
          let actualError = err.error || err.errorDetails?.error?.message || err.errorDetails?.message || `HTTP error! status: ${response.status}`;
          
          // If validation errors exist, format them into a readable message
          if (err.validationErrors && Array.isArray(err.validationErrors) && err.validationErrors.length > 0) {
            const validationMessages = err.validationErrors.map(ve => ve.message || ve.field).join(', ');
            actualError = `${actualError}. ${validationMessages}`;
          }
          
          const error = new Error(actualError);
          error.statusCode = response.status;
          error.errorDetails = err.errorDetails;
          error.validationErrors = err.validationErrors;
          error.rawErrorResponse = err.rawErrorResponse;
          
          console.error('🚨 [AI Service] Dimension assessment failed (streaming response):', {
            timestamp: new Date().toISOString(),
            email,
            status: response.status,
            statusText: response.statusText,
            errorMessage: actualError,
            errorResponse: err,
            errorDetails: err.errorDetails,
            validationErrors: err.validationErrors,
            rawErrorResponse: err.rawErrorResponse,
            stack: error.stack
          });
          throw error;
        }
        
        // Process SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep incomplete line
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'progress') {
                  onProgress(data.progress, data);
                } else if (data.type === 'html') {
                  // Forward HTML chunk to the progress callback for incremental rendering
                  onProgress(0, data);
                } else if (data.type === 'complete') {
                  console.log('[API] submitDimensionAssessment success (streaming)', data);
                  return { success: true, analysis: data.analysis, sessionId: data.sessionId, format: data.format };
                } else if (data.type === 'error') {
                  // Extract actual error message from server
                  // Handle ValidationError responses which may have validationErrors array
                  let actualErrorMsg = data.error || data.errorDetails?.error?.message || data.errorDetails?.message || 'Unknown error';
                  
                  // If validation errors exist, format them into a readable message
                  if (data.validationErrors && Array.isArray(data.validationErrors) && data.validationErrors.length > 0) {
                    const validationMessages = data.validationErrors.map(ve => ve.message || ve.field).join(', ');
                    actualErrorMsg = `${actualErrorMsg}. ${validationMessages}`;
                  }
                  
                  const error = new Error(actualErrorMsg);
                  error.statusCode = data.statusCode;
                  error.errorDetails = data.errorDetails;
                  error.validationErrors = data.validationErrors;
                  error.rawErrorResponse = data.rawErrorResponse;
                  
                  console.error('🚨 [AI Service] Dimension assessment error (SSE stream):', {
                    timestamp: new Date().toISOString(),
                    email,
                    streaming: true,
                    errorMessage: actualErrorMsg,
                    errorData: data,
                    errorDetails: data.errorDetails,
                    rawErrorResponse: data.rawErrorResponse,
                    stack: error.stack
                  });
                  throw error;
                }
              } catch (e) {
                console.warn('[API] Failed to parse SSE data:', e);
              }
            }
          }
        }
        
        throw new Error('Stream ended without completion');
      } else {
        // Regular (non-streaming) mode
        const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/assess-dimensions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        console.log('[API] submitDimensionAssessment status', response.status);
        if (!response.ok) {
          const err = await response.json().catch(()=>({}));
          // Extract actual error message from server response
          // Handle ValidationError responses which may have validationErrors array
          let actualError = err.error || err.errorDetails?.error?.message || err.errorDetails?.message || `HTTP error! status: ${response.status}`;
          
          // If validation errors exist, format them into a readable message
          if (err.validationErrors && Array.isArray(err.validationErrors) && err.validationErrors.length > 0) {
            const validationMessages = err.validationErrors.map(ve => ve.message || ve.field).join(', ');
            actualError = `${actualError}. ${validationMessages}`;
          }
          
          const error = new Error(actualError);
          error.statusCode = response.status;
          error.errorDetails = err.errorDetails;
          error.validationErrors = err.validationErrors;
          error.rawErrorResponse = err.rawErrorResponse;
          
          console.error('🚨 [AI Service] Dimension assessment failed (non-streaming):', {
            timestamp: new Date().toISOString(),
            email,
            status: response.status,
            statusText: response.statusText,
            errorMessage: actualError,
            errorResponse: err,
            errorDetails: err.errorDetails,
            rawErrorResponse: err.rawErrorResponse,
            stack: error.stack
          });
          throw error;
        }
        const data = await response.json();
        console.log('[API] submitDimensionAssessment success', data);
        return data;
      }
    } catch (error) {
      console.error('🚨 [AI Service] Dimension assessment error:', {
        timestamp: new Date().toISOString(),
        email,
        streaming: !!onProgress,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        errorCode: error.code,
        errorResponse: error.response
      });
      throw error;
    }
  }

  // Check if PDF exists for a session
  async checkPDFStatus(email, sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/sessions/${sessionId}/check-pdf`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.hasPDF === true;
    } catch (error) {
      console.error('Error checking PDF status:', error);
      return false;
    }
  }

  // Download PDF report (from stored files)
  async downloadPDFReport(email, sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/sessions/${sessionId}/download-report`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `MaturityReport.pdf`;
      
      // Convert response to blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Error downloading PDF report:', error);
      throw error;
    }
  }

  // Generate and download PDF report (requires sessionId)
  async generateAndDownloadPDFReport(email, sessionId) {
    try {
      if (!sessionId) {
        throw new Error('Session ID is required to generate PDF report');
      }
      
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/sessions/${sessionId}/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      // Get filename from Content-Disposition header with improved parsing
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = null;
      
      if (contentDisposition) {
        // Try multiple parsing methods to handle different header formats
        // Format 1: attachment; filename="filename.pdf"
        // Format 2: attachment; filename=filename.pdf
        // Format 3: attachment; filename*=UTF-8''filename.pdf
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '').trim();
        } else {
          // Fallback: try simple split method
          const parts = contentDisposition.split('filename=');
          if (parts.length > 1) {
            filename = parts[1].split(';')[0].replace(/['"]/g, '').trim();
          }
        }
      }
      
      // If still no filename, use a default (but log warning)
      if (!filename) {
        console.warn('[Client] Could not parse filename from Content-Disposition header:', contentDisposition);
        filename = `MaturityReport.pdf`; // Fallback
      }
      
      console.log('[Client] Content-Disposition header:', contentDisposition);
      console.log('[Client] Extracted filename:', filename);
      
      // Convert response to blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Error generating and downloading PDF report:', error);
      throw error;
    }
  }

  // Generate PDF report as blob (for sharing/email)
  async generatePDFReportAsBlob(email, sessionId) {
    try {
      if (!sessionId) {
        throw new Error('Session ID is required to generate PDF report');
      }

      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/sessions/${sessionId}/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `AI_Readiness_Report_${new Date().toISOString().split('T')[0]}.pdf`;

      // Convert response to blob
      const blob = await response.blob();

      return { blob, filename };
    } catch (error) {
      console.error('Error generating PDF report as blob:', error);
      throw error;
    }
  }

  // Validate custom answers
  async validateAnswers(answers, questions) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/validate-answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers, questions }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error validating answers:', error);
      throw error;
    }
  }

  // Download Word report
  async downloadWordReport(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/generate-report/word`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `MaturityReport.docx`;
      
      // Convert response to blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Error downloading Word report:', error);
      throw error;
    }
  }

  // Send PDF report via email
  async sendReportEmail(email, sessionId) {
    try {
      if (!sessionId) {
        // Silently fail - don't throw error
        console.warn('Session ID is required to send report email');
        return { success: false };
      }
      
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/send-report-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      // Always return success response - email is sent in background
      // Even if there's an error, we don't want to show it to the user
      if (!response.ok) {
        // Log error but don't throw - return success to user
        try {
          const errorData = await response.json();
          console.error('Email request error (silent):', errorData.error || errorData.message);
        } catch (parseError) {
          console.error('Email request error (silent):', response.status);
        }
        // Return success anyway - email will be handled in background
        return { success: true, message: 'Email request accepted' };
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      // Silently handle network errors - don't throw, just log
      console.error('Error sending report email (silent):', error);
      // Return success to user - email processing happens in background
      return { success: true, message: 'Email request accepted' };
    }
  }

  // Request guided assessment
  async requestGuidedAssessment(email, sessionId) {
    try {
      if (!sessionId) {
        throw new Error('Session ID is required to request guided assessment');
      }
      
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/request-guided-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error requesting guided assessment:', error);
      throw error;
    }
  }

  // Get user reports for dashboard
  async getUserReports(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/reports`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching user reports:', error);
      throw error;
    }
  }

  // Get user answers for a specific session (for viewing completed assessments)
  async getUserAnswers(email, sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/sessions/${sessionId}/answers`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching user answers:', error);
      throw error;
    }
  }

  // Discard current session
  async discardSession(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(email)}/discard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error discarding session:', error);
      throw error;
    }
  }

  // NEO Chatbot - Ask questions about dimensions and levels
  async askNeo(question, dimensionId = null, sessionId = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/neo/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question, dimensionId, sessionId })
      });
      
      const data = await response.json();
      
      // Even if response is not ok, return the data (it may contain a helpful error message)
      if (!response.ok) {
        // Extract actual error message from server response
        const actualError = data.error || data.errorDetails?.error?.message || data.errorDetails?.message || data.answer || `HTTP error! status: ${response.status}`;
        
        // If we have an answer in the response, use it; otherwise use error message
        const answerText = data.answer || actualError;
        
        console.error('🚨 [AI Service] NEO chatbot API error response:', {
          timestamp: new Date().toISOString(),
          question,
          dimensionId,
          sessionId,
          status: response.status,
          errorMessage: actualError,
          errorDetails: data.errorDetails,
          rawErrorResponse: data.rawErrorResponse
        });
        
        return { 
          success: false, 
          answer: answerText, 
          error: actualError,
          errorDetails: data.errorDetails,
          rawErrorResponse: data.rawErrorResponse,
          statusCode: response.status
        };
      }
      
      return data;
    } catch (error) {
      console.error('🚨 [AI Service] NEO chatbot error:', {
        timestamp: new Date().toISOString(),
        question,
        dimensionId,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        errorCode: error.code,
        errorResponse: error.response
      });
      // If it's a network error, provide a better message
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        throw new Error('Network error: Unable to connect to the server. Please check your connection.');
      }
      throw error;
    }
  }
}

export default new ApiService();
